import React, { useState } from 'react';
import { APIKeyCreateRequest, APIKey } from './apiKey';
import { apiKeyService } from './apiKeyService';
import { 
  Box, 
  Button, 
  Text,
  Heading,
  HStack,
  VStack,
  Stack
} from '@chakra-ui/react';
import StyledInput from '../Components/ui/styled-input';
import { ContentCard } from '../Components/ContentCard';

interface ApiKeyFormProps {
  apiKey?: APIKey;
  onSuccess: (apiKey: APIKey) => void;
  onCancel: () => void;
}

const ApiKeyForm: React.FC<ApiKeyFormProps> = ({ apiKey, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<APIKeyCreateRequest>({
    name: apiKey?.name || '',
    projectId: apiKey?.projectId || ''
  });
  
  const [errors, setErrors] = useState<{
    name?: string;
    projectId?: string;
  }>({});
  
  const [formError, setFormError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      projectId?: string;
    } = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    if (validateForm()) {
      try {
        const data = {
          name: formData.name.trim(),
          projectId: formData.projectId.trim() || undefined
        };
        
        let result: APIKey;
        
        if (apiKey) {
          // Update existing API key
          result = await apiKeyService.updateApiKey(apiKey.id, data);
        } else {
          // Create new API key
          result = await apiKeyService.createApiKey(data);
        }
        
        onSuccess(result);
      } catch (error) {
        console.error('Error submitting API key:', error);
        setFormError('Failed to ' + (apiKey ? 'update' : 'create') + ' API key. Please try again.');
      }
    }
  };

  return (
    <ContentCard>
      <Box as="form" onSubmit={handleSubmit}>
        <Heading as="h3" size="md" mb={4} color="gray.900">
          {apiKey ? 'Edit API Key' : 'Create New API Key'}
        </Heading>
        
        {formError && (
          <Box mb={4} p={3} borderRadius="lg" bg="red.50" borderWidth="1px" borderColor="red.200">
            <Text color="red.700">{formError}</Text>
          </Box>
        )}
        
        <VStack gap={4} align="stretch">
          <Stack>
            <Text fontWeight="medium" color="gray.900">API Key Name</Text>
            <StyledInput
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter a descriptive name"
              autoFocus
              borderColor={errors.name ? "red.300" : undefined}
            />
            {errors.name ? (
              <Text color="red.500" fontSize="sm">{errors.name}</Text>
            ) : (
              <Text fontSize="sm" color="gray.600">Give your API key a descriptive name</Text>
            )}
          </Stack>
          
          <Stack>
            <Text fontWeight="medium" color="gray.900">Project ID (Optional)</Text>
            <StyledInput
              name="projectId"
              value={formData.projectId || ''}
              onChange={handleChange}
              placeholder="Enter a project ID"
              borderColor={errors.projectId ? "red.300" : undefined}
            />
            {errors.projectId ? (
              <Text color="red.500" fontSize="sm">{errors.projectId}</Text>
            ) : (
              <Text fontSize="sm" color="gray.600">Associate this key with a specific project</Text>
            )}
          </Stack>
        </VStack>
        
        <HStack justifyContent="flex-end" mt={6} gap={3}>
          <Button onClick={onCancel} variant="outline" colorScheme="blue">Cancel</Button>
          <Button type="submit" colorScheme="blue">
            {apiKey ? 'Update' : 'Create'}
          </Button>
        </HStack>
      </Box>
    </ContentCard>
  );
};

export default ApiKeyForm;
